import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';

import { useGetOrgStructureItems } from '../../hooks/orgStructureItems/useGetOrgStructureItems';
import { useDeleteOrgStructureItem } from '../../hooks/orgStructureItems/useDeleteOrgStructureItem';
import { useDeleteAllOrgStructureItems } from '../../hooks/orgStructureItems/useDeleteAllOrgStructureItems';
import { useForceDeleteOrgStructureItem } from '../../hooks/orgStructureItems/useForceDeleteOrgStructureItem';
import { useForceDeleteAllOrgStructureItems } from '../../hooks/orgStructureItems/useForceDeleteAllOrgStructureItems';
import { useRestoreOrgStructureItem } from '../../hooks/orgStructureItems/useRestoreOrgStructureItem';
import { useRestoreAllOrgStructureItems } from '../../hooks/orgStructureItems/useRestoreAllOrgStructureItems';
import { usePermissions } from '../../auth/PermissionsContext';

import useTableActions from '../../hooks/common/useTableActions';
import TableLayout from '../global/Table/TableLayout';
import ArchiveButton from '../global/Table/ArchiveButton';
import OrgStructureItemsTableColumns from './Table/OrgStructureItemsTableColumns';
import OrgStructureItemsTableRow from './Table/OrgStructureItemsTableRow';

const OrgStructureItemsTable = ({ isArchive = false }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || 'en';
    const isArabic = lng === 'ar';
    const navigate = useNavigate();
    const { has } = usePermissions();

    const perms = useMemo(() => ({
        list: has('org_structure_items_list'),
        create: has('org_structure_items_create'),
        edit: has('org_structure_items_edit'),
        delete: has('org_structure_items_delete'),
        archive_list: has('org_structure_items_archive_list'),
        restore: has('org_structure_items_restore'),
        force_delete: has('org_structure_items_force_delete'),
    }), [has]);

    const { deleteOrgStructureItem, isDelete } = useDeleteOrgStructureItem();
    const { deleteAllOrgStructureItems, isDelete: isDeletingAll } = useDeleteAllOrgStructureItems();
    const { forceDeleteOrgStructureItem } = useForceDeleteOrgStructureItem();
    const { forceDeleteAllOrgStructureItems } = useForceDeleteAllOrgStructureItems();
    const { restoreOrgStructureItem } = useRestoreOrgStructureItem();
    const { restoreAllOrgStructureItems } = useRestoreAllOrgStructureItems();

    const {
        page, perPage, search, sortBy, sortDir, selectedIds, menuAnchorEl,
        setSearch, handleRequestSort, handleRowCheckboxClick, handleSelectAllClick,
        handleMenuOpen, handleMenuClose, handleEdit, handleMenuDelete, handleMenuRestore,
        handleBulkDelete, handleBulkRestore, handlePageChange, handlePerPageChange,
    } = useTableActions({
        basePath: '/org-structure-items',
        deleteSingle: deleteOrgStructureItem,
        deleteAll: deleteAllOrgStructureItems,
        forceDeleteSingle: forceDeleteOrgStructureItem,
        forceDeleteAll: forceDeleteAllOrgStructureItems,
        restoreSingle: restoreOrgStructureItem,
        restoreAll: restoreAllOrgStructureItems,
        isArchive,
        defaultSortBy: 'display_order',
        defaultSortDir: 'asc',
    });

    const filters = useMemo(() => ({
        search: search || undefined,
        sort_by: sortBy || undefined,
        sort_dir: sortDir || undefined,
        ...(isArchive ? { only_trashed: 1 } : {}),
    }), [search, sortBy, sortDir, isArchive]);

    const { isLoading, error, orgStructureItems, meta } = useGetOrgStructureItems(page, perPage, filters);

    const renderRow = (params) => (
        <OrgStructureItemsTableRow
            key={params.key || params.item.id}
            item={params.item} index={params.index}
            isArabic={params.isArabic} isDark={params.isDark}
            page={params.page} perPage={params.perPage} order={params.order}
            totalItems={orgStructureItems.length}
            selectedIds={params.selectedIds}
            onRowCheckboxClick={params.onRowCheckboxClick}
            onMenuOpen={params.onMenuOpen}
        />
    );

    return (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                {!isArchive && perms.archive_list && <ArchiveButton to="/org-structure-items/archive" />}
                {isArchive && perms.list && (
                    <ArchiveButton to="/org-structure-items" archiveLabelAr="العودة للقائمة" archiveLabelEn="Back to List" />
                )}
            </Box>

            <TableLayout
                title={isArabic ? (isArchive ? 'أرشيف عناصر الهيكل' : 'عناصر الهيكل التنظيمي') : (isArchive ? 'Org Structure Archive' : 'Org Structure Items')}
                subtitle={isArabic ? (isArchive ? 'إدارة العناصر المحذوفة' : 'إدارة تعريفات الهيكل التنظيمي') : (isArchive ? 'Manage deleted items' : 'Manage organizational structure definitions')}
                search={search} onSearchChange={setSearch}
                addButtonLabel={isArabic ? 'إضافة عنصر' : 'Add Item'}
                onAddClick={() => navigate('/org-structure-items/create')}
                addButtonVisible={!isArchive && perms.create}
                isLoading={isLoading} error={error}
                items={orgStructureItems} meta={meta}
                page={page} perPage={perPage}
                onPageChange={handlePageChange} onPerPageChange={handlePerPageChange}
                sortBy={sortBy} sortDir={sortDir} onRequestSort={handleRequestSort}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, orgStructureItems)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete}
                onBulkRestore={isArchive && perms.restore ? handleBulkRestore : undefined}
                isBulkDeleting={isDeletingAll}
                columns={OrgStructureItemsTableColumns} renderRow={renderRow}
                menuAnchorEl={menuAnchorEl} openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen} onMenuClose={handleMenuClose}
                onMenuEdit={!isArchive && perms.edit ? handleEdit : undefined}
                onMenuDelete={isArchive ? (perms.force_delete ? handleMenuDelete : undefined) : (perms.delete ? handleMenuDelete : undefined)}
                onMenuRestore={isArchive && perms.restore ? handleMenuRestore : undefined}
                menuDisableEdit={isArchive || !perms.edit}
                menuDisableDelete={isArchive ? !perms.force_delete : !perms.delete}
                menuDisableRestore={!isArchive || !perms.restore}
                isDeletingSingle={isDelete}
                emptyLabelAr={isArchive ? 'لا توجد عناصر في الأرشيف' : 'لا توجد عناصر هيكل تنظيمي'}
                emptyLabelEn={isArchive ? 'No archived items' : 'No org structure items'}
                loadErrorAr="حدث خطأ أثناء تحميل العناصر"
                loadErrorEn="Failed to load items"
            />
        </>
    );
};

export default OrgStructureItemsTable;
